import React, { useState, useEffect, useRef } from "react";
import emailjs from "@emailjs/browser";
import "./Inquiry.css";
import mainLogo from "../pictures/mainLogo.png";

const Inquiry = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    phone: ""
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ message: "", isSuccess: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const formRef = useRef();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First Name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid";
    if (!formData.phone.trim()) newErrors.phone = "Phone Number is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);

    // First email (full form)
    emailjs
      .sendForm(
        "service_yajzu9u",
        "template_v6c00pn",
        formRef.current,
        "hAJmqdyYf07E3qAtY"
      )
      .then(() => {
        // Second email (only firstName, lastName, email)
        const secondEmailData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email
        };

        emailjs.send(
          "service_yajzu9u",
          "template_2a4xs4o",
          secondEmailData,
          "hAJmqdyYf07E3qAtY"
        ).catch((err) => {
          console.error("Second EmailJS Error:", err);
        });

        setStatus({ message: "Thank you! Your request has been submitted successfully.", isSuccess: true });
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          message: "",
          phone: ""
        });
        formRef.current.reset();
        setIsSubmitting(false);
        setTimeout(() => setStatus({ message: "", isSuccess: true }), 5000);
      })
      .catch((error) => {
        console.error("EmailJS Error:", error);
        setStatus({ message: "Something went wrong. Please try again.", isSuccess: false });
        setIsSubmitting(false);
        setTimeout(() => setStatus({ message: "", isSuccess: true }), 5000);
      });
  };

  return (
    <div className="inquiry-page">
      <div className="background-blobs">
        <div className="blob blob-1" style={{ transform: `translateY(${scrollY * 0.3}px)` }}></div>
        <div className="blob blob-2" style={{ transform: `translateY(${scrollY * 0.2}px)` }}></div>
        <div className="blob blob-3" style={{ transform: `translateY(${scrollY * 0.15}px)` }}></div>
      </div>

      <header className="inquiry-header">
        <div className="header-top">
          <div className="logo-container">
            <img src={mainLogo} alt="MG & J's General Logo" className="main-logo" />
            <div className="logo-text">
              <h1>MG & J's General, LLC.</h1>
              <p>Historic Restoration, Property Maintenance, and Construction</p>
            </div>
          </div>
          <div className="hero-section">
            <h2 className="inquiry-h2-resize">Request a Service/Job</h2>
            <p className="inquiry-p-resize">Tell us about your project and we'll get back to you!</p>
          </div>
        </div>
      </header>

      <main className="inquiry-main">
        <div className="inquiry-wrapper">
          <div className="form-card">
            <form ref={formRef} onSubmit={handleSubmit} className="inquiry-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="firstName"
                    defaultValue={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? "error-input" : ""}
                    placeholder="John"
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>

                <div className="form-group">
                  <label>Last Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="lastName"
                    defaultValue={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? "error-input" : ""}
                    placeholder="Doe"
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={formData.email}
                    onChange={handleChange}
                    className={errors.email ? "error-input" : ""}
                    placeholder="john@example.com"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label>Phone <span className="required">*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    defaultValue={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? "error-input" : ""}
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>What is the Job? <span className="required">*</span></label>
                <textarea
                  name="message"
                  rows="6"
                  defaultValue={formData.message}
                  onChange={handleChange}
                  className={errors.message ? "error-input" : ""}
                  placeholder="Please describe your project in detail..."
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              {status.message && (
                <div
                  className="status-banner"
                  style={{
                    backgroundColor: status.isSuccess ? "#4CAF50" : "#f44336",
                    color: "#fff",
                    padding: "10px",
                    marginBottom: "10px",
                    borderRadius: "5px",
                    textAlign: "center"
                  }}
                >
                  {status.message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="submit-button"
              >
                {isSubmitting ? "Submitting..." : "Submit Request"}
              </button>
            </form>
          </div>

          <div className="trust-indicators">
            <div className="trust-card"><div className="trust-number">48h</div><p>Response Time</p></div>
            <div className="trust-card"><div className="trust-number">100%</div><p>Customer Satisfaction</p></div>
            <div className="trust-card"><div className="trust-number">10+</div><p>Years Experience</p></div>
          </div>
        </div>
      </main>

      <footer className="inquiry-footer">
        <p>© 2025 MG & J's General, LLC. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Inquiry;
